/**
 * RLQ — ClaimAndStart
 *
 * FSR claims a lobby entry → auto-navigates to the appropriate module
 * (RAO for new-account, RAM for maintenance, RCM for info-update, etc.)
 * with CIF preloaded.
 *
 * This component is normally reached via `/rlq/claim/:sessionId`. It is NOT a
 * standalone page — it's a transient "claiming…" splash.
 *
 * v0.1.22 fix (Bill 2026-04-22): earlier version gated navigation on
 * `Promise.allSettled([updateLastAction, events.write])`. Both calls wait on
 * async auth-token acquisition; if either path stalls (Teams JS not booted,
 * MSAL silent-token loop, 503-without-body, etc.) the claim splash hangs
 * indefinitely. The target URL is computable synchronously from query params,
 * so navigation never needs to wait on a network write. Telemetry writes are
 * now true fire-and-forget with a 5-second guardrail that logs failures to
 * the client-error buffer (so regressions show up in
 * `window.__PCB_ERRORS__`) but never delay the operator.
 */
import { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { WaitStateOverlay, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { appendCapturedError } from '@/hooks/useClientErrorCapture';
import { asIso8601 } from '@retail-ops/shared-types';
import type { SessionEventPayload } from '@retail-ops/shared-types';

import type { ServiceTypeTargetModule } from './types';

/** Maps a target module to the shell's URL route. */
const MODULE_ROUTE: Record<ServiceTypeTargetModule, string> = {
  RAO: '/rao',
  RAM: '/ram',
  RCM: '/rcm',
  RIM: '/rim',
  RCD: '/rcd',
  RSM: '/rsm',
  RSC: '/rsc',
  RCC: '/rcc',
};

/** How long to show the splash before auto-navigating, in ms. */
const SPLASH_DELAY_MS = 250;

/** Hard cap on telemetry writes — if they take longer, we still log. */
const TELEMETRY_TIMEOUT_MS = 5000;

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T | { __timedOut: true; label: string }> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<{ __timedOut: true; label: string }>((resolve) => {
    timer = setTimeout(() => resolve({ __timedOut: true, label }), ms);
  });
  return Promise.race([
    p.finally(() => { if (timer) clearTimeout(timer); }),
    timeout,
  ]);
}

export default function ClaimAndStart(): JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const { user } = useAuth();

  // Params passed from the queue row — module target + CIF if resolved.
  const targetModule =
    (search.get('target') as ServiceTypeTargetModule | null) ?? 'RCC';
  const cifId = search.get('cifId') ?? '';
  const cartId = search.get('cartId') ?? '';

  // Target URL is computed synchronously — no network required.
  const targetUrl = useMemo(() => {
    const base = MODULE_ROUTE[targetModule] ?? '/rop';
    const qp: string[] = [];
    if (cifId) qp.push(`cifId=${encodeURIComponent(cifId)}`);
    if (cartId) qp.push(`cartId=${encodeURIComponent(cartId)}`);
    if (sessionId) qp.push(`sessionId=${encodeURIComponent(sessionId)}`);
    return qp.length > 0 ? `${base}?${qp.join('&')}` : base;
  }, [targetModule, cifId, cartId, sessionId]);

  // Navigate after a tiny splash delay; telemetry fires in the background.
  useEffect(() => {
    if (!sessionId) return;

    // Fire-and-forget: audit the claim + stamp the session. Guardrailed with
    // a 5s timeout so a hanging Function call can't leak a pending promise
    // forever. Failures land in `window.__PCB_ERRORS__` for diagnostics.
    const claimPayload: SessionEventPayload = {
      sessionId,
      eventType: 'queue-claimed',
      eventCategory: 'session',
      severity: 'info',
      actor: user?.upn ?? 'fsr',
      timestamp: asIso8601(new Date().toISOString()),
      payloadJson: JSON.stringify({ targetModule, cifId, cartId }),
    };

    void withTimeout(
      client.retail.sessions.updateLastAction(sessionId, {
        actionCode: 'claim',
        actionContext: targetModule,
      }),
      TELEMETRY_TIMEOUT_MS,
      'v3.sessions.updateLastAction',
    ).then((result) => {
      if (result && typeof result === 'object' && '__timedOut' in result) {
        appendCapturedError({
          when: new Date().toISOString(),
          kind: 'telemetry-timeout',
          message: `claim audit stamp timed out after ${TELEMETRY_TIMEOUT_MS}ms`,
          stack: '',
          componentStack: 'ClaimAndStart',
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        });
      }
    }).catch((err: unknown) => {
      appendCapturedError({
        when: new Date().toISOString(),
        kind: 'telemetry-failure',
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? (err.stack ?? '') : '',
        componentStack: 'ClaimAndStart → updateLastAction',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
    });

    void withTimeout(
      client.retail.events.write(claimPayload),
      TELEMETRY_TIMEOUT_MS,
      'v3.events.write',
    ).then((result) => {
      if (result && typeof result === 'object' && '__timedOut' in result) {
        appendCapturedError({
          when: new Date().toISOString(),
          kind: 'telemetry-timeout',
          message: `queue-claimed event write timed out after ${TELEMETRY_TIMEOUT_MS}ms`,
          stack: '',
          componentStack: 'ClaimAndStart',
          url: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        });
      }
    }).catch((err: unknown) => {
      appendCapturedError({
        when: new Date().toISOString(),
        kind: 'telemetry-failure',
        message: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? (err.stack ?? '') : '',
        componentStack: 'ClaimAndStart → events.write',
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
    });

    // Navigate immediately after a tiny splash so the operator sees
    // acknowledgment of the click before the target module mounts.
    const t = window.setTimeout(() => navigate(targetUrl, { replace: true }), SPLASH_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [sessionId, targetUrl, targetModule, cifId, cartId, client, user, navigate]);

  if (!sessionId) {
    return (
      <div className="rlq-page">
        <div className="rlq-claim">
          <h2 className="rlq-claim__missing-title">Missing session id</h2>
          <p>No lobby entry was specified. Return to the queue and try again.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/rlq', { replace: true })}
          >
            Back to lobby queue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rlq-page">
      <div className="rlq-claim">
        <WaitStateOverlay
          headline="Claiming lobby entry"
          status={`Routing you to ${targetModule}…`}
        />
        <div className="rlq-claim__ghost">
          <div className="rlq-claim__pill-row">
            <Pill variant="info">Target: {targetModule}</Pill>
            {cifId && <Pill variant="muted">CIF {cifId}</Pill>}
            {cartId && <Pill variant="muted">Cart {cartId.slice(0, 8)}</Pill>}
          </div>
          <div className="rlq-claim__session">
            Session <code>{sessionId}</code>
          </div>
        </div>
        {/* Manual bail-out if the splash lingers for any reason. */}
        <button
          type="button"
          className="btn btn-ghost btn-sm rlq-claim__continue"
          onClick={() => navigate(targetUrl, { replace: true })}
        >
          Continue to {targetModule} now →
        </button>
      </div>
    </div>
  );
}
